// AddSlideModal.tsx (Corrected Full File)
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Modal, Card, Button } from '@dopomogai/ui';
import { motion } from 'framer-motion';
import { renderIcon } from '@dopomogai/utils';
import { SlideData, SlideTemplate } from '@dopomogai/types';
import { slideTemplates } from '@dopomogai/data'; // Assuming this is where your slideTemplates array is
import clsx from 'clsx';


interface AddSlideModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAddSlide: (slide: SlideData) => void;
}

export const AddSlideModal: React.FC<AddSlideModalProps> = ({
  isOpen,
  onClose,
  onAddSlide
}) => {
  const { t } = useTranslation(['templates', 'common']);

  const handleSelectTemplate = (template: SlideTemplate) => {
    // THE FIX:
    // The `template.defaultContent` is now expected to already be a string
    // representing a function that takes `({ data, theme })` and returns JSX.
    // We no longer need to add an extra `(props) => (...)` wrapper here,
    // as that caused the double-wrapping error.
    const newSlide: SlideData = {
      id: `slide-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      title: template.title,
      type: template.type,
      content: template.defaultContent, // Use defaultContent directly
      order: 999, // This will be re-calculated in App.tsx by handleUpdatePresentation
      notes: '',
    };
    onAddSlide(newSlide);
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={t('title', 'Add a New Slide')} size="xl">
        <div className="p-2">
            <p className="text-slate-600 mb-6 text-center">
              {t('choose_template_desc', 'Select a template to start with. You can customize it later.')}
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-h-[60vh] overflow-y-auto p-2">
              {slideTemplates.map((template, index) => (
                <motion.div
                  key={template.type + index}
                  // Staggered entry animation (this is fine)
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: index * 0.05 }}
                  // ADDED: Framer Motion interaction animations
                  whileHover={{ y: -4, x: -4, boxShadow: '4px 4px 0px 0px rgb(17 24 39)' }}
                  whileTap={{ y: 0, x: 0, boxShadow: 'none' }}
                  
                  role="button"
                  tabIndex={0}
                  onClick={() => handleSelectTemplate(template)}
                  onKeyDown={(e) => { if(e.key === 'Enter' || e.key === ' ') handleSelectTemplate(template) }}
                  // Use clsx for base styling only. REMOVED conflicting hover/active classes.
                  className={clsx(
                    'cursor-pointer bg-white rounded-xl border-2 border-black p-4'
                  )}
                >
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-emerald-100 rounded-lg flex items-center justify-center border-2 border-black">
                        {renderIcon(template.icon, { size: 24, className: 'text-emerald-700' })}
                    </div>
                    <div className="flex-1">
                        <h3 className="text-lg font-semibold text-slate-900">
                        {t(`templates:${template.type}.name`, { defaultValue: template.title })}
                        </h3>
                        <p className="text-sm text-slate-600">
                        {t(`templates:${template.type}.description`, { defaultValue: template.description })}
                        </p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>

            <div className="mt-6 flex justify-end">
              <Button variant="outline" onClick={onClose} text={t('common:cancel', 'Cancel')} />
            </div>
        </div>
    </Modal>
  );
};